import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DivergingLikertPlot, quickStudyDivergingLikertAdapter } from '@digital-twins/deck-ui/plots/diverging-likert'

import { Result } from './quickStudyTypes'

import { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * A quick-study rating read drawn from Python's `diverging_likert_plot`
 * (W6-QS): the survey tab's `diverging-likert` plot, with its Average | Top N
 * (or Average | NPS) views, counts and base, one row per monadic item. Every
 * figure is Python's. The tab and Average words fall back to the app's
 * viewer-language keys. Returns null when the result carries no plot (stored
 * results): the caller keeps its legacy reader.
 *
 * One switch per choice: when the card holds the metric switch (`controlled`),
 * the view is the card's and the plot draws no tabs of its own. An NPS read
 * keeps its count legend ("Detractors (0-6): 12 (30%)"), every figure stamped:
 * the shared plot draws it (`showCountLegend`, W7 Q17).
 */

export interface QuickStudyLikertPlotProps {
  result: Pick<Result, 'diverging_likert_plot'>
  /** The view to draw: the card's metric when `controlled`, else the one to open on. */
  view?: PlotStatView | null
  /** True when the card's metric switch holds the view (the plot then draws no tabs). */
  controlled?: boolean
  /** False where the card prints the base once (the chat footnote). Default true. */
  showBase?: boolean
  testIdPrefix?: string
}

const VIEW_BY_METRIC: Record<string, PlotStatView> = {
  mean: 'mean',
  top_2_box: 'topBoxes',
  nps: 'nps',
}

/** The plot view a quick-study metric id names, or null for any other id. */
export function likertViewForMetric(metric: string | null | undefined): PlotStatView | null {
  return metric ? VIEW_BY_METRIC[metric] ?? null : null
}

/** Whether a result can draw through `QuickStudyLikertPlot` (a stamped plot is present). */
export function hasQuickStudyLikertPlot(result: Pick<Result, 'diverging_likert_plot'>): boolean {
  return quickStudyDivergingLikertAdapter.accepts(result)
}

export function QuickStudyLikertPlot(props: QuickStudyLikertPlotProps) {
  const { result, view = null, controlled = false, showBase = true, testIdPrefix = 'quick-study-likert' } = props

  const [heldView, setHeldView] = useState<PlotStatView | null>(view)

  const { t, i18n } = useTranslation('app')

  if (!quickStudyDivergingLikertAdapter.accepts(result)) {
    return null
  }
  const { data } = quickStudyDivergingLikertAdapter.read(result)
  const fallback = {
    average: t('studyReport.numericChart.average'),
    nps: t('studyReport.statTabNps'),
  }
  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <DivergingLikertPlot
        data={data}
        activeView={controlled ? view : heldView}
        onViewChange={setHeldView}
        locale={i18n.language}
        tabLabelFallback={fallback}
        testIdPrefix={`${testIdPrefix}-plot`}
        showTabs={!controlled}
        showCountLegend
        showBase={showBase}
        showScaleEnds
      />
    </div>
  )
}
